import AppLayoutReduced from 'components/AppLayout/AppLayoutReduced';
import CallToAction from 'components/CallToAction';
import CardWrapper from 'components/CardWrapper';
import CloseButton from 'components/CloseButton';
import CompanyLogo from 'components/CompanyLogo';
import ErrorMessage from 'components/ErrorMessage';
import Form from 'components/Form';
import FormTagList from 'components/FormTagList';
import Input from 'components/Inputs/Input';
import Textarea from 'components/Inputs/Textarea';
import ColumnWrapper from 'components/Layout/ColumnWrapper';
import OverlayWrapper from 'components/Layout/OverlayWrapper';
import PositionWrapper from 'components/Layout/PositionWrapper/PositionWrapper';
import ProjectWrapper from 'components/Layout/ProjectWrapper';
import RainbowEditWrapper from 'components/Layout/RainbowEditWrapper';
import RowWrapper from 'components/Layout/RowWrapper';
import MainButton from 'components/MainButton';
import PageHeader from 'components/PageHeader';
import PageLogoHeader from 'components/PageLogoHeader';
import ToggleSwitch from 'components/ToggleSwitch';
import UploadButton from 'components/UploadButton';
import UploadedImage from 'components/UploadedImage';
import UploadContainer from 'containers/UploadContainer';
import {
  UploadContainerChildProps,
  UploadContainerStateEnum
} from 'containers/UploadContainer/UploadContainer';
import UploadedFileContainer from 'containers/UploadContainer/UploadedFileContainer';
import { inject, observer } from 'mobx-react';
import { ElementType } from 'models/ApiElementTypeEnum';
import { AttachmentsStoreType } from 'models/AttachmentsStore';
import { DataStoreType } from 'models/DataStore';
import { OrganizationsStoreType } from 'models/OrganizationsStore';
import React from 'react';
import {
  FormattedMessage,
  injectIntl,
  WrappedComponentProps
} from 'react-intl';
import { ROUTE_CUSTOMERS_BASE, ROUTE_LAB } from 'utils/constants/routes';
import { HistoryProps } from 'utils/history';
import useForm, { FormType, handleFormError } from 'utils/hooks/useForm';

interface CreateCustomerScreenState {}
interface CreateCustomerScreenProps
  extends HistoryProps,
    WrappedComponentProps {
  organizationsStore: OrganizationsStoreType;
  dataStore: DataStoreType;
  form: FormType;
  attachmentsStore: AttachmentsStoreType;
}

@inject('dataStore', 'organizationsStore', 'attachmentsStore')
@observer
class CreateCustomerScreen extends React.Component<
  CreateCustomerScreenProps,
  CreateCustomerScreenState
> {
  async addCustomer() {
    const { form, organizationsStore, dataStore, history, attachmentsStore } =
      this.props;
    if (!dataStore.currentOrganization) {
      return;
    }

    form.resetErrors();

    const customer = {
      name: form.values.name || '',
      sector: form.values.sector || '',
      services: {
        name: form.values['services.name'] || '',
        content: form.values['services.content'] || ''
      },
      target_groups: {
        name: form.values['target_groups.name'] || '',
        content: form.values['target_groups.content'] || ''
      },
      network_enabled: form.values['network enabled'] || false
    };
    try {
      const newCustomer = await organizationsStore.createCustomer(
        dataStore.currentOrganizationId!,
        customer
      );

      if (!organizationsStore.isItemSaveError) {
        if (newCustomer && newCustomer.id) {
          await attachmentsStore.upload(
            form.values.file,
            'logo',
            ElementType.Organization,
            newCustomer.id
          );
          history.replace(ROUTE_LAB + '/' + newCustomer.id);
        } else {
          history.replace(ROUTE_LAB + '/' + dataStore.currentOrganizationId);
        }
      }
    } catch (error: any) {
      console.log(error.body.errors);
      handleFormError(form, error);
    }
  }

  uploadLoadingStateChanged(newState?: UploadContainerStateEnum) {
    const { form, dataStore } = this.props;

    if (!newState) {
      form.setLoading(false);
      return;
    }

    if (newState === 'saving' && !dataStore.benchmarkItem?.id) {
      // item has not been saved yet, so we need to wait for the upload before we can reenable form
      form.setLoading(true);
    }
  }

  setFile(file: File | undefined) {
    const { form } = this.props;
    form.setField('file', file);
  }

  attachmentRemoved(id: number) {
    const {
      dataStore: { benchmarkItem }
    } = this.props;
    if (!benchmarkItem) {
      return;
    }

    benchmarkItem.attachments.delete(id);
  }

  renderPage(content: any) {
    const { dataStore, intl, history } = this.props;

    let header;

    const org = dataStore.currentOrganization;
    if (org) {
      const logo = org.attachments.firstOfType('logo');
      header = (
        <PageLogoHeader
          big={true}
          title={org.name}
          subtitle={org.sector}
          logo={
            logo &&
            logo.resource_urls && (
              <CompanyLogo
                imgUrl={logo.resource_urls.small}
                alt="Logo"
                big={true}
              />
            )
          }
        />
      );
    }

    return (
      <AppLayoutReduced active="lab" withSettings={true}>
        <PageHeader
          reduced={true}
          titleId="Our projects(titleId)"
          logoHeader={header}
        />
        {content}
        <OverlayWrapper topRight={true}>
          <CloseButton
            label={intl.formatMessage({ id: 'Close' })}
            iconName="cross"
            onClick={() => {
              history.push(
                ROUTE_CUSTOMERS_BASE + '/' + dataStore.currentOrganizationId
              );
            }}
          />
        </OverlayWrapper>
      </AppLayoutReduced>
    );
  }

  renderError() {
    return this.renderPage(<ErrorMessage state="save_error" />);
  }

  render() {
    const { organizationsStore, dataStore, form } = this.props;

    const org = dataStore.currentOrganization;
    if (!org) {
      return this.renderError();
    }

    return this.renderPage(
      <>
        <Form loading={form.loading} onSubmit={() => this.addCustomer()}>
          <ProjectWrapper>
            <FormTagList form={form} field="tags" allowAdd={true} />

            <ColumnWrapper gap="2em">
              {organizationsStore.isItemSaveError && (
                <ErrorMessage
                  state={organizationsStore.itemLoadingState}
                  onRetry={() => this.addCustomer()}
                />
              )}

              <ColumnWrapper>
                <RainbowEditWrapper customer={true}>
                  {form.values.file && (
                    <UploadedFileContainer
                      file={form.values.file}
                      onAttachmentRemoved={() => this.setFile(undefined)}
                      key={form.values.file.id}
                    >
                      {(props) => <UploadedImage logo={true} {...props} />}
                    </UploadedFileContainer>
                  )}
                  {!form.values.file && (
                    <UploadContainer
                      attachmentType="logo"
                      elementType={ElementType.Organization}
                      elementId={undefined}
                      acceptVideo={true}
                      preview={true}
                      onFilePreview={(file) => this.setFile(file)}
                    >
                      {(props: UploadContainerChildProps) => (
                        <UploadButton {...props} />
                      )}
                    </UploadContainer>
                  )}

                  <CardWrapper>
                    <Input
                      name="name"
                      label={<FormattedMessage id="Customer name" />}
                      autoFocus={true}
                      maxLength={30}
                      {...form.bindInput('name')}
                    />
                  </CardWrapper>
                </RainbowEditWrapper>
              </ColumnWrapper>

              <CardWrapper>
                <Input
                  name="sector"
                  label={<FormattedMessage id="Sector" />}
                  maxLength={30}
                  {...form.bindInput('sector')}
                />
              </CardWrapper>

              <CardWrapper>
                {form.errors['services'] && (
                  <div className="input-state__error">
                    {form.errors['services']}
                  </div>
                )}
                <Input
                  gap={true}
                  name="service_name"
                  label={<FormattedMessage id="customer form service name" />}
                  maxLength={30}
                  {...form.bindInput('services.name')}
                />
                <Textarea
                  gap={true}
                  name="service_content"
                  label={<FormattedMessage id="customer form service description" />}
                  {...form.bindInput('services.content')}
                  lines={4}
                />
              </CardWrapper>

              <CardWrapper>
                {form.errors['target_groups'] && (
                  <div className="input-state__error">
                    {form.errors['target_groups']}
                  </div>
                )}
                <Input
                  gap={true}
                  name="target_group_name"
                  label={<FormattedMessage id="customer form target group name" />}
                  maxLength={30}
                  {...form.bindInput('target_groups.name')}
                />
                <Textarea
                  gap={true}
                  name="target_group_content"
                  label={<FormattedMessage id="customer form target group description" />}
                  {...form.bindInput('target_groups.content')}
                  lines={4}
                />
              </CardWrapper>

              <PositionWrapper center={true}>
                <RowWrapper gap="1em">
                  <ToggleSwitch
                    label={<FormattedMessage id="Customer network enabled" />}
                    {...form.bindCheckbox('network enabled')}
                  />
                  <span aria-hidden="true">
                    <FormattedMessage id="Project network enabled" />
                  </span>
                </RowWrapper>
              </PositionWrapper>

              <PositionWrapper center={true}>
                <MainButton type="submit">
                  <FormattedMessage id="Customer create" />
                </MainButton>
              </PositionWrapper>
            </ColumnWrapper>
          </ProjectWrapper>
        </Form>

        <CallToAction />
      </>
    );
  }
}

export default injectIntl((props: CreateCustomerScreenProps) => {
  const form = useForm();
  // @ts-ignore
  return <CreateCustomerScreen {...props} form={form} />;
});
